// MapToolbar.jsx with improved AOI handling
import React, { useRef, useState, useEffect } from 'react';
import L from 'leaflet';
import { Layers, Pencil, Upload, ZoomIn, ZoomOut, Square, Edit, Trash2 } from 'lucide-react';
import StraightenIcon from '@mui/icons-material/Straighten';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure.css';
import { kml } from '@tmcw/togeojson';
import shp from 'shpjs';


const buttonStyle = {
  background: 'none',
  border: 'none',
  borderBottom: '1px solid rgba(255,255,255,0.2)',
  padding: '10px',
  cursor: 'pointer',
  color: 'white',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative'
};

const activeButtonStyle = {
  ...buttonStyle,
  background: 'rgba(255,255,255,0.2)',
};

const tooltipStyle = {
  position: 'absolute',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  color: 'white',
  padding: '5px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 1001,
  opacity: 0,
  transition: 'opacity 0.2s',
  visibility: 'hidden'
};

const MapToolbar = ({
onResetFilters,
  map, 
  baseLayers = {}, 
  activeLayer, 
  setActiveLayer, 
  drawMode, 
  setDrawMode,
  position = 'left',
  toolbarStyle = {},
  onAOIDeleted,
  onDrawToolbarToggle
}) => {
  const [showLayerSelect, setShowLayerSelect] = useState(false);
  const [showDrawOptions, setShowDrawOptions] = useState(false);
  const [measurementActive, setMeasurementActive] = useState(false);
  const [cursorTooltip, setCursorTooltip] = useState({
    display: false,
    text: '',
    x: 0,
    y: 0
  });
  
  const fileInputRef = useRef(null);
  const measurePolylineRef = useRef(null);
  const measurePointsRef = useRef([]);
  const measureMarkersRef = useRef([]);
  
  // Refs for tooltip targets
  const layersButtonRef = useRef(null);
  const measureButtonRef = useRef(null);
  const drawButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const zoomInButtonRef = useRef(null);
  const zoomOutButtonRef = useRef(null);
  const uploadedLayersRef = useRef([]);

  const clearUploadedLayers = () => {
    uploadedLayersRef.current.forEach(layer => {
      if (layer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    uploadedLayersRef.current = [];
  };
  
  

  // Add custom CSS for the measurement tooltip
  useEffect(() => {
    // Add custom CSS for the tooltip
    const style = document.createElement('style');
    style.textContent = `
      .distance-tooltip {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .distance-tooltip .leaflet-tooltip-content {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      .leaflet-tooltip-top:before {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    
    // Clean up
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Show/hide tooltip functions
  const showTooltip = (e) => {
    const tooltip = e.currentTarget.querySelector('.tooltip');
    if (tooltip) {
      tooltip.style.opacity = '1';
      tooltip.style.visibility = 'visible';
      
      if (position === 'left') {
        tooltip.style.left = '45px';
        tooltip.style.right = 'auto';
      } else {
        tooltip.style.right = '45px';
        tooltip.style.left = 'auto';
      }
    }
  };

  const hideTooltip = (e) => {
    const tooltip = e.currentTarget.querySelector('.tooltip');
    if (tooltip) {
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
    }
  };

  // Function to calculate distance between two points in meters
  const calculateDistance = (latlng1, latlng2) => {
    return map.distance(latlng1, latlng2);
  };

  // Format distance for display
  const formatDistance = (distanceInMeters) => {
    if (distanceInMeters < 1000) {
      return `${distanceInMeters.toFixed(1)} m`;
    } else {
      return `${(distanceInMeters / 1000).toFixed(2)} km`;
    }
  };

  // Hide cursor tooltip - separate from cleanup to avoid redundancy
  const hideCursorTooltip = () => {
    setCursorTooltip({
      display: false,
      text: '',
      x: 0,
      y: 0
    });
  };

  // Handle map click for measuring
  const handleMapClick = (e) => {
    // Only check if the polyline exists - don't check measurementActive 
    // since state updates are asynchronous and might not be updated yet
    if (!measurePolylineRef.current) {
      return;
    }
    
    const newPoint = e.latlng;
    
    // If we already have two points, clear everything
    if (measurePointsRef.current.length === 2) {
      // Remove existing polyline
      if (measurePolylineRef.current) {
        map.removeLayer(measurePolylineRef.current);
      }
      
      // Remove existing markers
      measureMarkersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      
      // Reset collections
      measurePointsRef.current = [];
      measureMarkersRef.current = [];
      
      // Create new polyline
      measurePolylineRef.current = L.polyline([], {
        color: '#2E90FA',
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5',
        lineCap: 'round'
      }).addTo(map);
    }
    
    // Add the new point
    measurePointsRef.current.push(newPoint);
    
    // Add a marker
    const marker = L.circleMarker(newPoint, {
      radius: 4,
      color: '#2E90FA',
      fillColor: '#2E90FA',
      fillOpacity: 1,
      weight: 2
    }).addTo(map);
    measureMarkersRef.current.push(marker);
    
    // Update the polyline
    if (measurePolylineRef.current) {
      measurePolylineRef.current.setLatLngs(measurePointsRef.current);
    }
    
    // If this is the second point, complete the measurement
    if (measurePointsRef.current.length === 2) {
      // Remove the dashed style
      if (measurePolylineRef.current) {
        measurePolylineRef.current.setStyle({
          dashArray: null,
          weight: 4
        });
      }
      
      // Calculate distance
      const point1 = measurePointsRef.current[0];
      const point2 = measurePointsRef.current[1];
      const distance = calculateDistance(point1, point2);
      const formattedDistance = formatDistance(distance);
      
      // Create a midpoint for the label
      const midpoint = L.latLng(
        (point1.lat + point2.lat) / 2,
        (point1.lng + point2.lng) / 2
      );
      
      // Add a tooltip with the distance
      const tooltip = L.tooltip({
        permanent: true,
        direction: 'top',
        className: 'distance-tooltip',
        offset: [0, -10],
        opacity: 1.0
      })
      .setLatLng(midpoint)
      .setContent(`<div style="background-color: rgba(33, 150, 243, 0.8); color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;">${formattedDistance}</div>`)
      .addTo(map);
      
      measureMarkersRef.current.push(tooltip);
      
      // Hide the cursor tooltip
      hideCursorTooltip();
    }
  };

  // Handle mouse move for live preview
  const handleMouseMove = (e) => {
    // Only check if the polyline exists - don't check measurementActive
    // since state updates are asynchronous and might not be updated yet
    if (!measurePolylineRef.current) {
      return;
    }
    
    if (measurePointsRef.current.length === 1) {
      // Create a temporary array with the first point and mouse position
      const tempPoints = [measurePointsRef.current[0], e.latlng];
      measurePolylineRef.current.setLatLngs(tempPoints);
      
      // Calculate the distance
      const distance = calculateDistance(tempPoints[0], tempPoints[1]);
      const formattedDistance = formatDistance(distance);
      
      // Update cursor tooltip state
      setCursorTooltip({
        display: true,
        text: formattedDistance,
        x: e.containerPoint.x + 15,
        y: e.containerPoint.y - 15
      });
    }
  };

  // Toggle measurement mode on/off
  const toggleMeasurement = () => {
    if (measurementActive) {
      // Turn off measurement mode - the useEffect will handle cleanup
      setMeasurementActive(false);
      setDrawMode(null);
    } else {
      // Turn on measurement mode - the useEffect will handle setup
      setMeasurementActive(true);
      setDrawMode('measure');
      
      // Close other menus
      setShowLayerSelect(false);
      setShowDrawOptions(false);
    }
  };

  const handleLayers = () => {
    // Close draw options if open
    if (showDrawOptions) {
      setShowDrawOptions(false);
    }
    setShowLayerSelect(!showLayerSelect);
  };

  const handleDrawButton = () => {
    // Close layer select if open
    if (showLayerSelect) {
      setShowLayerSelect(false);
    }
    const newShowDrawOptions = !showDrawOptions;
    setShowDrawOptions(newShowDrawOptions);
    
    // Notify parent when draw toolbar is opened/closed
    if (onDrawToolbarToggle) {
      onDrawToolbarToggle(newShowDrawOptions);
    }
    
    if (drawMode) {
      disableAllDrawModes();
    }
  };

  const disableAllDrawModes = () => {
    if (!map) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode();
    map.pm.disableGlobalRemovalMode();
    
    // Also clean up measurement if active, but don't call toggleMeasurement
    // which can cause state timing issues
    if (measurementActive) {
      setMeasurementActive(false);
    }
    
    setDrawMode(null);
  };


useEffect(() => {
  if (!map || !onResetFilters) return;

  // Fires after user finishes drawing rectangle or polygon
  const handleShapeCreated = (e) => {
    if (e.layer instanceof L.Polygon || e.layer instanceof L.Rectangle) {
      onResetFilters();   // 🔥 Reset filters AFTER draw completes
    }
  };

  map.on("pm:create", handleShapeCreated);

  return () => {
    map.off("pm:create", handleShapeCreated);
  };
}, [map, onResetFilters]);

  const handleDrawMode = (mode) => {
    if (!map) return;
//    if (mode === 'polygon' || mode === 'rectangle') {
//      onResetFilters();   // 🔥 Trigger PanelContent reset
//    }

    disableAllDrawModes();
    
    switch(mode) {
      case 'rectangle':
        map.pm.enableDraw('Rectangle');
        setDrawMode('rectangle');
        break;
      case 'polygon':
        map.pm.enableDraw('Polygon');
        setDrawMode('polygon');
        break;
      case 'edit':
        map.pm.enableGlobalEditMode();
        setDrawMode('edit');
        break;
      case 'finishEdit':
        // Before disabling the edit mode, get the updated coordinates from active AOI polygon
        if (map && drawMode === 'edit') {
          // Find the active AOI polygon by looking for polygons with the specific color
          let updatedCoordinates = null;
          map.eachLayer((layer) => {
            // Check if it's a polygon with our AOI styling
            if (layer instanceof L.Polygon && 
                layer.options && 
                layer.options.color === '#2E90FA' && 
                layer.options.fillOpacity === 0) {
              
              // Get the updated coordinates
              const latLngs = layer.getLatLngs()[0];
              updatedCoordinates = latLngs.map(latLng => ({
                lat: latLng.lat,
                lng: latLng.lng
              }));
            }
          });
          
          // If we found updated coordinates, store them
          if (updatedCoordinates) {
            // Trigger a custom event that ExplorePage can listen for
            const customEvent = new CustomEvent('aoiCoordinatesUpdated', {
              detail: { coordinates: updatedCoordinates }
            });
            window.dispatchEvent(customEvent);
          }
        }
        
        // Disable edit mode, which will trigger pm:globaleditmodetoggled event
        map.pm.disableGlobalEditMode();
        setDrawMode(null);
        break;
      case 'delete':
        map.pm.enableGlobalRemovalMode();
        setDrawMode('delete');
        
        // Set up a one-time listener for the next removal
        const removedHandler = () => {
          // Call the parent component's callback when AOI is deleted
          if (onAOIDeleted && typeof onAOIDeleted === 'function') {
            onAOIDeleted();
          }
          
          // Remove this listener after it's triggered once
          map.off('pm:remove', removedHandler);
        };
        
        map.on('pm:remove', removedHandler);
        break;
      default:
        break;
    }
    setShowDrawOptions(false);
  };
  

  const switchLayer = (layerId) => {
    if (!map) return;
    
    // Remove current active layer
    if (baseLayers[activeLayer]) {
      baseLayers[activeLayer].remove();
    }
    
    // Add new layer
    if (baseLayers[layerId]) {
      baseLayers[layerId].addTo(map);
      setActiveLayer(layerId);
    }
    
    setShowLayerSelect(false);
  };

  const handleUpload = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (!map || !fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!map || !file) return;
  
    try {
      const filename = file.name.toLowerCase();
      let geojsonData;
  
      if (filename.endsWith('.geojson')) {
        const content = await file.text();
        geojsonData = JSON.parse(content);
      } else if (filename.endsWith('.kml')) {
        const text = await file.text();
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(text, 'text/xml');
        geojsonData = kml(kmlDoc);
      } else if (filename.endsWith('.zip')) {
        const buffer = await file.arrayBuffer();
        geojsonData = await shp(buffer);
      } else {
        alert("Unsupported file type.");
        return;
      }
  
      // ✅ Remove all shape-related layers
      map.eachLayer((layer) => {
        if (layer instanceof L.Polygon || 
            layer instanceof L.Polyline || 
            layer instanceof L.Marker || 
            layer instanceof L.Circle || 
            layer instanceof L.CircleMarker || 
            layer instanceof L.GeoJSON) {
          map.removeLayer(layer);
        }
      });
  
      // ✅ Add new upload
      const geojsonLayer = L.geoJSON(geojsonData).addTo(map);
      map.fitBounds(geojsonLayer.getBounds());
      // Extract coordinates from first polygon feature
geojsonLayer.eachLayer((layer) => {
  if (layer instanceof L.Polygon || layer instanceof L.MultiPolygon) {
    const latLngs = layer.getLatLngs()[0];
    const coordinates = latLngs.map(latLng => ({
      lat: latLng.lat,
      lng: latLng.lng
    }));

    const customEvent = new CustomEvent('aoiCoordinatesUpdated', {
      detail: { coordinates }
    });
    window.dispatchEvent(customEvent);
  }
});

  
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Failed to load the file.');
    }
  };
  
  
  
  

  const handleZoomIn = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (map && map.getZoom() !== undefined) {
      map.setZoom(map.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    // Close all open dropdowns
    setShowLayerSelect(false);
    setShowDrawOptions(false);
    
    if (map && map.getZoom() !== undefined) {
      map.setZoom(map.getZoom() - 1);
    }
  };


    useEffect(() => {
      if (!map) return;

      const handleEditEnd = (e) => {
        e.layer.eachLayer((layer) => {
          if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
            const latLngs = layer.getLatLngs()[0];
            const coordinates = latLngs.map(latLng => ({
              lat: latLng.lat,
              lng: latLng.lng
            }));

            console.log('✏️ AOI edited:', coordinates);
            window.dispatchEvent(
              new CustomEvent('aoiCoordinatesUpdated', { detail: { coordinates } })
            );
          }
        });
      };

      map.on('pm:edit', handleEditEnd);

      return () => map.off('pm:edit', handleEditEnd);
    }, [map]);


  useEffect(() => {
      if (!map) return;

      // Handler for completed draw event
      const handleDrawEnd = (e) => {
        const layer = e.layer;
        if (!layer || !(layer instanceof L.Polygon || layer instanceof L.Rectangle)) return;

        // Extract coordinates
        const latLngs = layer.getLatLngs()[0];
        const coordinates = latLngs.map(latLng => ({
          lat: latLng.lat,
          lng: latLng.lng
        }));

        console.log('🟩 AOI drawn:', coordinates);

        // Dispatch global event so PanelContent can react
        window.dispatchEvent(
          new CustomEvent('aoiCoordinatesUpdated', { detail: { coordinates } })
        );

        // Optionally fit bounds or zoom to the AOI
        map.fitBounds(layer.getBounds());
      };

      // Register once when component mounts
      map.on('pm:create', handleDrawEnd);

      // Clean up
      return () => {
        map.off('pm:create', handleDrawEnd);
      };
    }, [map]);


  // Single consolidated effect to handle event listeners
  useEffect(() => {
    if (!map) return;
    
    // Clean up function to ensure proper cleanup
    const cleanup = () => {
      map.off('click', handleMapClick);
      map.off('mousemove', handleMouseMove);
      
      if (measurePolylineRef.current) {
        map.removeLayer(measurePolylineRef.current);
        measurePolylineRef.current = null;
      }
      
      // Remove all markers
      measureMarkersRef.current.forEach(marker => {
        if (marker) map.removeLayer(marker);
      });
      
      measurePointsRef.current = [];
      measureMarkersRef.current = [];
    };
    
    // If measurement is active, add event listeners
    if (measurementActive) {
      // Clean up first to ensure no duplicates
      cleanup();
      
      // Create a new polyline
      measurePolylineRef.current = L.polyline([], {
        color: '#2E90FA',
        weight: 3,
        opacity: 0.8,
        dashArray: '5, 5',
        lineCap: 'round'
      }).addTo(map);
      
      map.on('click', handleMapClick);
      map.on('mousemove', handleMouseMove);
    } else {
      // Clean up when measurement is deactivated
      cleanup();
    }
    
    // Return the cleanup function for component unmount
    return cleanup;
  }, [map, measurementActive]);

  const defaultPosition = {
    position: 'fixed',
    [position]: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgb(33, 150, 243)',
    width: '40px',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    ...toolbarStyle
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        onChange={handleFileChange}
        accept=".geojson,.kml,.gpx,.shp,.zip"
      />
      <div style={defaultPosition}>
        {/* Draw Tools */}
        <div style={{ position: 'relative' }}>
          <button 
            ref={drawButtonRef}
            onClick={handleDrawButton}
            style={drawMode && !measurementActive ? activeButtonStyle : buttonStyle}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <Pencil size={18} />
            <div className="tooltip" style={tooltipStyle}>Draw Tools</div>
          </button>
          {showDrawOptions && (
            <div style={{
              position: 'absolute',
              [position === 'left' ? 'left' : 'right']: '100%',
              top: 0,
              marginLeft: position === 'left' ? '10px' : '-130px',
              backgroundColor: 'rgb(33, 150, 243)',
              borderRadius: '4px',
              padding: '4px',
              width: '120px'
            }}>
              <button onClick={() => handleDrawMode('rectangle')} style={drawMode === 'rectangle' ? activeButtonStyle : buttonStyle}>
                <Square size={14} /> Rectangle
              </button>
              <button onClick={() => handleDrawMode('polygon')} style={drawMode === 'polygon' ? activeButtonStyle : buttonStyle}>
                <Pencil size={14} /> Polygon
              </button>
              <button onClick={() => handleDrawMode('edit')} style={drawMode === 'edit' ? activeButtonStyle : buttonStyle}>
                <Edit size={14} /> Edit
              </button>
            </div>
          )}
        </div>

        {/* Upload GeoJSON */}
        <button 
          ref={uploadButtonRef}
          onClick={handleUpload}
          style={buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <Upload size={18} />
          <div className="tooltip" style={tooltipStyle}>Upload AOI</div>
        </button>
  
        {/* Map Layers */}
        <div style={{ position: 'relative' }}>
          <button 
            ref={layersButtonRef}
            onClick={handleLayers}
            style={showLayerSelect ? activeButtonStyle : buttonStyle}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <Layers size={18} />
            <div className="tooltip" style={tooltipStyle}>Map Layers</div>
          </button>
          {showLayerSelect && (
            <div style={{
              position: 'absolute',
              [position === 'left' ? 'left' : 'right']: '100%',
              top: 0,
              marginLeft: position === 'left' ? '10px' : '-130px',
              backgroundColor: 'rgb(33, 150, 243)',
              borderRadius: '4px',
              padding: '4px',
              width: '120px'
            }}>
              {Object.keys(baseLayers).map((layerId) => (
                <button
                  key={layerId}
                  onClick={() => switchLayer(layerId)}
                  style={{
                    background: layerId === activeLayer ? 'rgba(255,255,255,0.2)' : 'none',
                    border: 'none',
                    padding: '8px 12px',
                    color: 'white',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {layerId}
                </button>
              ))}
            </div>
          )}
        </div>
  
        {/* Measure Distance */}
        <button 
          ref={measureButtonRef}
          onClick={toggleMeasurement}
          style={measurementActive ? activeButtonStyle : buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <StraightenIcon style={{ fontSize: '18px' }} />
          <div className="tooltip" style={tooltipStyle}>Measure Distance</div>
        </button>
  
        {/* Zoom In */}
        <button 
          ref={zoomInButtonRef}
          onClick={handleZoomIn}
          style={buttonStyle}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <ZoomIn size={18} />
          <div className="tooltip" style={tooltipStyle}>Zoom In</div>
        </button>
  
        {/* Zoom Out */}
        <button 
          ref={zoomOutButtonRef}
          onClick={handleZoomOut}
          style={{
            ...buttonStyle,
            borderBottom: 'none'
          }}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <ZoomOut size={18} />
          <div className="tooltip" style={tooltipStyle}>Zoom Out</div>
        </button>
      </div>
  
      {/* Cursor tooltip for distance measurement */}
      {cursorTooltip.display && (
        <div style={{
          position: 'absolute',
          left: `${cursorTooltip.x}px`,
          top: `${cursorTooltip.y}px`,
          backgroundColor: 'rgb(33, 150, 243)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          pointerEvents: 'none',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          {cursorTooltip.text}
        </div>
      )}
  
      {/* Finish Edit Floating Button */}
      {drawMode === 'edit' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgb(76, 175, 80)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          zIndex: 1500,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 'bold'
        }}
        onClick={() => handleDrawMode('finishEdit')}
        >
          <Edit size={16} /> Finish Editing
        </div>
      )}
    </>
  );
};
export default MapToolbar;